import React, { useState, useMemo, useRef } from 'react';
import { FileText, Upload, Download, ChevronRight, ChevronDown, Search, Image, ImageOff, Link2, X, Square, FolderOpen, Check, Ban, Palette, Monitor, Layers, ListChecks, Rocket, LayoutGrid } from 'lucide-react';
import { getPresetTemplates, getBackgroundStyles, BACKGROUND_PRESETS, BOPPPS_SECTION_OPTIONS } from '../utils/presetTemplates';
import { createSlideTemplate } from '../utils/slideTemplate';
import ImageLinkInput from './ImageLinkInput';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT, openFileInput } from '../utils/filePicker';
import { rememberOpenedFileName } from '../utils/deckVersioning';
import {
  describeLoadError,
  parsePresentationText,
  OpenPresentationError,
  OpenPresentationErrorType
} from '../features/openPresentation';
import PresentationLoadErrorScreen from './PresentationLoadErrorScreen';

// The blank PowerPoint deck is the default starting point: it owns the first
// tab, so the screen opens on a single obvious choice and one Start button.
const DEFAULT_TEMPLATE_ID = 'pptx-blank';

// The screen asks one question — start blank, or start from a template — and
// answers it with a tab each. Everything needed to start a deck lives inside
// the tab that offers it, so a Start button is never hunting for its own
// settings on another part of the page.
const TAB_START = 'start';
const TAB_LIBRARY = 'library';

const EXPORT_GROUPS = [
  {
    id: 'presentation',
    title: 'Slide Canvas · Best for PowerPoint Export',
    shortLabel: 'PowerPoint',
    description: 'Pick these for fixed 16:9 slide layouts where drag, scale, and position decisions should match the PowerPoint export.',
    detail: 'PowerPoint templates use a canvas-driven slide system. Each slide behaves like a freehand design surface where content blocks can be positioned, scaled, layered, and arranged with depth control so the exported PPTX matches the layout you designed.',
    icon: Square,
    accent: 'orange',
    templateIds: ['pptx-blank', 'pptx-ready']
  },
  {
    id: 'html-flow',
    title: 'Flow Design · Best for HTML Export',
    shortLabel: 'HTML Flow',
    description: 'Choose these when your lesson should read as a guided web page with natural vertical flow, richer content blocks, and responsive HTML export.',
    detail: 'Flow Design templates use a stacked card system. Content is organized from top to bottom in guided lesson sections, making them best for responsive HTML lessons where cards naturally reflow for different screens.',
    icon: Monitor,
    accent: 'blue',
    templateIds: ['modern-professional', 'creative-education', 'comprehensive']
  },
  {
    id: 'combined',
    title: 'Flexible Blank · HTML Export',
    shortLabel: 'HTML Blank',
    description: 'Use these neutral starters when you want a clean BOPPPS structure and an HTML-friendly blank lesson foundation.',
    detail: 'Flexible Blank templates start with minimal structure and use the same flow-driven, stacked-card editing model as HTML lessons. They are best when you want to build the content sequence yourself without committing to a styled example.',
    icon: Layers,
    accent: 'purple',
    templateIds: ['empty', 'minimal']
  }
];

const TEMPLATE_EXPORT_INFO = EXPORT_GROUPS.reduce((acc, group) => {
  group.templateIds.forEach((id) => {
    acc[id] = group;
  });
  return acc;
}, {});

const getTemplateExportGroup = (template) => TEMPLATE_EXPORT_INFO[template.id] || EXPORT_GROUPS[1];

const getGroupAccentClasses = (accent) => ({
  blue: 'border-blue-800/50 bg-blue-950/20 text-blue-300',
  purple: 'border-purple-800/50 bg-purple-950/20 text-purple-300',
  orange: 'border-orange-800/50 bg-orange-950/20 text-orange-300'
}[accent] || 'border-gray-700 bg-gray-800 text-gray-300');

// Template thumbnail previews with visual representation
const TemplateThumbnail = ({ template, isSelected, onClick, customBackground }) => {
  const exportGroup = getTemplateExportGroup(template);
  // Use custom background if provided, otherwise use template's default
  const backgroundToUse = customBackground || template.bodyBackground;
  const bgStyles = backgroundToUse
    ? getBackgroundStyles(backgroundToUse)
    : { backgroundColor: '#ffffff' };

  return (
    <div
      onClick={onClick}
      className={`
        relative cursor-pointer rounded-lg overflow-hidden transition-all duration-200
        border-2 ${isSelected ? 'border-blue-500 shadow-lg scale-[1.02]' : 'border-gray-700 hover:border-gray-500 hover:shadow-md'}
      `}
    >
      {/* Preview thumbnail */}
      <div
        className="aspect-video relative"
        style={{
          ...bgStyles,
          minHeight: '140px'
        }}
      >
        {/* Header bar preview */}
        <div
          className="absolute top-0 left-0 right-0 h-6"
          style={{ backgroundColor: template.headerColor || '#64748b' }}
        />

        {/* Content preview shapes */}
        <div className="absolute inset-0 p-4 pt-10 flex flex-col gap-2">
          {/* Title line */}
          <div
            className="h-3 rounded"
            style={{
              backgroundColor: template.headerColor || '#64748b',
              width: '60%',
              opacity: 0.8
            }}
          />
          {/* Content lines */}
          <div className="flex gap-2 mt-2">
            <div className="flex-1 h-8 rounded bg-gray-200 opacity-60" />
            <div className="flex-1 h-8 rounded bg-gray-200 opacity-60" />
          </div>
          <div className="h-2 rounded bg-gray-200 opacity-40 w-full mt-1" />
          <div className="h-2 rounded bg-gray-200 opacity-40 w-3/4" />
        </div>

        {/* Category badge. The slide templates say what shape they are rather
            than naming PowerPoint again: they export to it, they are not it. */}
        <div
          className="absolute top-8 right-2 px-2 py-0.5 rounded text-xs font-medium text-white"
          style={{ backgroundColor: template.headerColor || '#64748b' }}
        >
          {template.category === 'basic' && 'Basic'}
          {template.category === 'professional' && 'Professional'}
          {template.category === 'education' && 'Education'}
          {template.category === 'comprehensive' && 'Full Demo'}
          {template.category === 'presentation' && '16:9 slides'}
        </div>
      </div>

      {/* Template info */}
      <div className="p-3 bg-gray-800">
        <h3 className="font-semibold text-gray-100 text-sm">{template.name}</h3>
        {/* A template's own subtitle wins over the export-group label, so the
            blank deck reads "PowerPoint · Compatible" rather than saying
            PowerPoint twice. */}
        <div className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium mb-2 ${getGroupAccentClasses(exportGroup.accent)}`}>
          {template.subtitle || exportGroup.shortLabel}
        </div>
        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{template.description}</p>
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 left-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
};

// Server-hosted background image presets, shared by both pickers
const getBackgroundImagePresets = () =>
  Object.entries(BACKGROUND_PRESETS)
    .filter(([, preset]) => preset.type === 'image')
    .map(([key, preset]) => ({ key, ...preset }));

// Accent class sets keyed by the picker's theme color
const ACCENTS = {
  blue: {
    border: 'border-blue-500',
    ring: 'ring-blue-400/60',
    badge: 'bg-blue-500',
    icon: 'text-blue-400',
    heading: 'text-gray-100',
    panel: 'border-gray-700'
  },
  purple: {
    border: 'border-purple-500',
    ring: 'ring-purple-400/60',
    badge: 'bg-purple-500',
    icon: 'text-purple-400',
    heading: 'text-purple-200',
    panel: 'border-purple-900/50'
  },
  amber: {
    border: 'border-amber-500',
    ring: 'ring-amber-400/60',
    badge: 'bg-amber-500',
    icon: 'text-amber-400',
    heading: 'text-amber-200',
    panel: 'border-amber-900/50'
  }
};

// Preset header-bar colors (from the institutional palette): metallic gold
// (Pantone 873C) and standard black.
const HEADER_COLOR_PRESETS = [
  { hex: '#BF9C66', name: 'Gold (873C)' },
  { hex: '#000000', name: 'Black' }
];

// A single selectable tile — the shared building block for every background
// option so the whole control reads as one coherent gallery.
const BackgroundTile = ({ selected, onClick, title, label, accent, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="group flex flex-col items-center gap-1.5 focus:outline-none"
  >
    <div
      className={`relative w-full aspect-video rounded-lg border-2 overflow-hidden transition-all ${
        selected
          ? `${accent.border} ring-2 ${accent.ring}`
          : 'border-gray-600 hover:border-gray-400'
      }`}
    >
      {children}
      {selected && (
        <span className={`absolute top-1 right-1 w-4 h-4 ${accent.badge} rounded-full flex items-center justify-center shadow`}>
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        </span>
      )}
    </div>
    <span className={`text-[11px] leading-tight text-center w-full truncate ${selected ? 'text-gray-100 font-medium' : 'text-gray-400'}`}>
      {label}
    </span>
  </button>
);

// Thumbnail for a preset background. If the artwork ever fails to load the
// tile falls back to the preset's edge colour instead of leaving the browser
// to render bare alt text over an empty box.
const PresetTileImage = ({ preset }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: preset.backgroundColor || '#e5e7eb' }}
      >
        <ImageOff className="w-4 h-4 text-gray-500" />
      </div>
    );
  }

  return (
    <img
      src={preset.preview}
      alt={preset.name}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Unified background picker used for both the slide and title-card
 * backgrounds. Every option — the default, the optional "same as slide"
 * choice, each preset image, and the custom upload — renders as an equal
 * sized labeled tile in one gallery grid, replacing the older mix of
 * full-width buttons, a cramped thumbnail strip, and a separate upload row.
 */
const BackgroundPicker = ({
  label,
  hint,
  icon: Icon = Image,
  accent: accentKey = 'blue',
  defaultLabel = 'Template default',
  selectedBackground,
  onSelectBackground,
  onUploadCustom,
  customImage,
  customId = 'custom',
  sameAsSlide = null // { styles } to render a "Same as slide" tile, or null
}) => {
  const fileInputRef = useRef(null);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const accent = ACCENTS[accentKey] || ACCENTS.blue;
  const images = getBackgroundImagePresets();

  // A linked background fills the same custom slot an upload does — same
  // object, same preview tile — with an address in place of the file's bytes.
  const applyLinkedImage = (url, result) => {
    onUploadCustom({
      id: customId,
      name: result?.name || 'Linked image',
      type: 'image',
      value: url,
      backgroundSize: 'cover',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat'
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        onUploadCustom({
          id: customId,
          name: file.name,
          type: 'image',
          value: event.target.result,
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat'
        });
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const isCustomSelected = typeof selectedBackground === 'object' && selectedBackground?.id === customId;
  const isSameAsSlide = selectedBackground === 'use-slide-background';

  return (
    <div className={`bg-gray-800/60 rounded-xl p-4 border ${accent.panel}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent.icon}`} />
        <h4 className={`text-sm font-semibold ${accent.heading}`}>{label}</h4>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-0.5 mb-3">{hint}</p>}
      {!hint && <div className="mb-3" />}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {/* Default (no image) */}
        <BackgroundTile
          selected={!selectedBackground}
          onClick={() => onSelectBackground(null)}
          title={defaultLabel}
          label={defaultLabel}
          accent={accent}
        >
          <div className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-500">
            <Ban className="w-5 h-5" />
          </div>
        </BackgroundTile>

        {/* Optional "same as slide" tile (title card only) */}
        {sameAsSlide && (
          <BackgroundTile
            selected={isSameAsSlide}
            onClick={() => onSelectBackground('use-slide-background')}
            title="Same as slide background"
            label="Same as slide"
            accent={accent}
          >
            <div className="w-full h-full" style={sameAsSlide.styles} />
          </BackgroundTile>
        )}

        {/* Preset images */}
        {images.map((bg) => (
          <BackgroundTile
            key={bg.key}
            selected={selectedBackground === bg.key}
            onClick={() => onSelectBackground(bg.key)}
            title={bg.name}
            label={bg.name}
            accent={accent}
          >
            <PresetTileImage preset={bg} />
          </BackgroundTile>
        ))}

        {/* Custom upload / preview */}
        <div className="flex flex-col items-center gap-1.5">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          <div
            className={`relative w-full aspect-video rounded-lg border-2 overflow-hidden transition-all ${
              isCustomSelected
                ? `${accent.border} ring-2 ${accent.ring}`
                : 'border-dashed border-gray-600 hover:border-gray-400'
            }`}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-full flex items-center justify-center bg-gray-700/50 hover:bg-gray-700"
              title={isCustomSelected ? 'Change custom image' : 'Upload a custom image'}
            >
              {isCustomSelected && customImage ? (
                <img src={customImage.value} alt="Custom background" className="w-full h-full object-cover" />
              ) : (
                <Upload className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {isCustomSelected && (
              <button
                type="button"
                onClick={() => onSelectBackground(null)}
                className="absolute top-1 right-1 w-4 h-4 bg-red-500/90 hover:bg-red-500 rounded-full flex items-center justify-center shadow"
                title="Remove custom image"
              >
                <X className="w-3 h-3 text-white" strokeWidth={3} />
              </button>
            )}
          </div>
          <span className={`text-[11px] leading-tight text-center w-full truncate ${isCustomSelected ? 'text-gray-100 font-medium' : 'text-gray-400'}`}>
            {isCustomSelected ? 'Custom' : 'Upload'}
          </span>
        </div>

        {/* Link — the same custom slot, filled with an address instead of a
            copy, so a big picture costs the deck nothing. */}
        <button
          type="button"
          onClick={() => setIsLinkOpen((open) => !open)}
          title="Use a picture that is already hosted somewhere"
          aria-expanded={isLinkOpen}
          className="group flex flex-col items-center gap-1.5 focus:outline-none"
        >
          <div
            className={`relative w-full aspect-video rounded-lg border-2 border-dashed overflow-hidden transition-all flex items-center justify-center bg-gray-700/50 hover:bg-gray-700 ${
              isLinkOpen ? accent.border : 'border-gray-600 hover:border-gray-400'
            }`}
          >
            <Link2 className="w-5 h-5 text-gray-400" />
          </div>
          <span className={`text-[11px] leading-tight text-center w-full truncate ${isLinkOpen ? 'text-gray-100 font-medium' : 'text-gray-400'}`}>
            Link
          </span>
        </button>
      </div>

      {isLinkOpen && (
        <div className="mt-4 rounded-lg border border-gray-700 bg-gray-900/50 p-3">
          <ImageLinkInput
            tone="dark"
            label="Use a linked image"
            onUseLink={applyLinkedImage}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Header-bar color picker: the same tile gallery as the background pickers,
 * but the tiles are solid color swatches — the template default, the preset
 * palette colors, and a custom color the user can pick or set exactly.
 */
const HeaderColorPicker = ({
  label = 'Slide Header',
  hint,
  icon: Icon = Palette,
  accent: accentKey = 'amber',
  templateDefaultColor = '#545f7d',
  selectedColor,
  onSelectColor
}) => {
  const colorInputRef = useRef(null);
  const accent = ACCENTS[accentKey] || ACCENTS.blue;
  const normalized = (selectedColor || '').toLowerCase();
  const isPreset = HEADER_COLOR_PRESETS.some((preset) => preset.hex.toLowerCase() === normalized);
  const isCustom = Boolean(selectedColor) && !isPreset;

  return (
    <div className={`bg-gray-800/60 rounded-xl p-4 border ${accent.panel}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent.icon}`} />
        <h4 className={`text-sm font-semibold ${accent.heading}`}>{label}</h4>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-0.5 mb-3">{hint}</p>}
      {!hint && <div className="mb-3" />}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {/* Template default color */}
        <BackgroundTile
          selected={!selectedColor}
          onClick={() => onSelectColor(null)}
          title="Template default"
          label="Template default"
          accent={accent}
        >
          <div className="w-full h-full" style={{ backgroundColor: templateDefaultColor }} />
        </BackgroundTile>

        {/* Preset palette colors */}
        {HEADER_COLOR_PRESETS.map((preset) => (
          <BackgroundTile
            key={preset.hex}
            selected={normalized === preset.hex.toLowerCase()}
            onClick={() => onSelectColor(preset.hex)}
            title={`${preset.name} · ${preset.hex}`}
            label={preset.name}
            accent={accent}
          >
            <div className="w-full h-full" style={{ backgroundColor: preset.hex }} />
          </BackgroundTile>
        ))}

        {/* Custom color */}
        <div className="flex flex-col items-center gap-1.5">
          <div
            className={`relative w-full aspect-video rounded-lg border-2 overflow-hidden transition-all ${
              isCustom ? `${accent.border} ring-2 ${accent.ring}` : 'border-gray-600 hover:border-gray-400'
            }`}
          >
            <button
              type="button"
              onClick={() => colorInputRef.current?.click()}
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: isCustom ? selectedColor : '#374151' }}
              title="Pick a custom header color"
            >
              {!isCustom && <Palette className="w-5 h-5 text-gray-400" />}
              {isCustom && (
                <span className={`absolute top-1 right-1 w-4 h-4 ${accent.badge} rounded-full flex items-center justify-center shadow`}>
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
              )}
            </button>
            <input
              ref={colorInputRef}
              type="color"
              value={isCustom ? selectedColor : '#545f7d'}
              onChange={(e) => onSelectColor(e.target.value)}
              className="sr-only"
              aria-label="Custom header color"
            />
          </div>
          <span className={`text-[11px] leading-tight text-center w-full truncate ${isCustom ? 'text-gray-100 font-medium' : 'text-gray-400'}`}>
            {isCustom ? selectedColor.toUpperCase() : 'Custom'}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * One fold-out shelf of the template library. Collapsed by default so the
 * screen opens with a single recommended starting point instead of every
 * template at once; new template families become new shelves.
 */
const LibraryShelf = ({ group, isOpen, onToggle, children }) => {
  const GroupIcon = group.icon;
  const panelId = `template-shelf-${group.id}`;

  return (
    <section className="border-t border-gray-800 first:border-t-0">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-800/40 transition-colors"
        >
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0 ${getGroupAccentClasses(group.accent)}`}>
            <GroupIcon className="w-4 h-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-100">{group.title}</span>
            <span className="block text-xs text-gray-500 truncate">{group.description}</span>
          </span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {group.templates.length} template{group.templates.length === 1 ? '' : 's'}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </h3>
      {isOpen && (
        <div id={panelId} className="px-5 pb-5">
          {children}
        </div>
      )}
    </section>
  );
};

// What a deck built here can become once it leaves the app. The card is named
// "PowerPoint" because that is what it exports to; this is the rest of the
// answer, and the reason the name is not a promise that the app *is* PowerPoint.
const EXPORT_FORMATS = [
  {
    label: 'PowerPoint (.pptx)',
    description: 'Opens and keeps editing in PowerPoint, Keynote or Google Slides — pick this deck when the lesson has to be handed over as slides.'
  },
  {
    label: 'PDF',
    description: 'A fixed copy for printing, posting to a course shell, or presenting from anywhere.'
  },
  {
    label: 'HTML',
    description: 'The same lesson as a web page students can scroll, with the interactive blocks still working.'
  },
  {
    label: 'JSON',
    description: 'Save your work and load it back here later. Nothing is stored on a server.'
  }
];

/**
 * What a deck built here can become once it leaves the app.
 *
 * The card above is called "PowerPoint" because that is what it exports to,
 * and this says the rest: the editor is a browser, the 16:9 canvas is a
 * promise about the exported file, and the same lesson also leaves as a PDF,
 * a web page or a file you can reopen here.
 */
const ExportSummary = () => (
  <div className="mt-6 rounded-xl border border-gray-700 bg-gray-800/60 p-4">
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <div className="flex items-center gap-2">
        <Download className="w-4 h-4 text-orange-400" />
        <h3 className="text-sm font-semibold text-gray-100">What you can export</h3>
      </div>
      <p className="text-xs text-gray-400">
        This is a slide editor in your browser, not Microsoft PowerPoint. Everything sits on a
        16:9 canvas, so what you lay out here is what comes out of the file.
      </p>
    </div>
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {EXPORT_FORMATS.map((format) => (
        <div key={format.label} className="rounded-lg border border-gray-700 bg-gray-900/40 p-3">
          <div className="text-xs font-semibold text-gray-100">{format.label}</div>
          <p className="text-[11px] leading-snug text-gray-400 mt-1">{format.description}</p>
        </div>
      ))}
    </div>
  </div>
);

/**
 * One of the screen's two tabs. Wide, labelled with what it is *for* rather
 * than what it contains, so the choice reads as "start blank" or "browse
 * templates" before any of the detail underneath is taken in.
 */
const StartTab = ({ id, icon: Icon, title, description, isActive, onSelect }) => (
  <button
    type="button"
    role="tab"
    id={`template-tab-${id}`}
    aria-selected={isActive}
    aria-controls={`template-tabpanel-${id}`}
    onClick={onSelect}
    className={`flex flex-1 items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
      isActive
        ? 'border-blue-500 bg-blue-950/40 shadow-sm'
        : 'border-gray-700 bg-gray-900/40 hover:border-gray-600 hover:bg-gray-800/40'
    }`}
  >
    <span
      className={`w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0 ${
        isActive ? 'border-blue-500/60 bg-blue-900/40 text-blue-300' : 'border-gray-700 bg-gray-800 text-gray-400'
      }`}
    >
      <Icon className="w-4 h-4" />
    </span>
    <span className="min-w-0">
      <span className={`block text-sm font-semibold ${isActive ? 'text-gray-100' : 'text-gray-300'}`}>{title}</span>
      <span className="block text-xs text-gray-500 leading-tight">{description}</span>
    </span>
  </button>
);

/**
 * Optional BOPPPS scaffolding for the blank PowerPoint deck: each section can
 * be switched on individually, and with everything off the deck stays a single
 * empty slide.
 */
const BopppsSectionPicker = ({ selectedIds, onChange }) => {
  const allIds = BOPPPS_SECTION_OPTIONS.map((section) => section.id);
  const allSelected = selectedIds.length === allIds.length;

  const toggle = (id) => {
    onChange(selectedIds.includes(id)
      ? selectedIds.filter((selected) => selected !== id)
      : allIds.filter((candidate) => selectedIds.includes(candidate) || candidate === id));
  };

  return (
    <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-orange-400" />
          <h4 className="text-sm font-semibold text-gray-100">BOPPPS Sections</h4>
        </div>
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : allIds)}
          className="text-xs text-blue-400 hover:text-blue-300"
        >
          {allSelected ? 'Turn all off' : 'Turn all on'}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        Off by default — switch on the sections you want and the blank deck starts with one empty slide for each.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {BOPPPS_SECTION_OPTIONS.map((section) => {
          const isOn = selectedIds.includes(section.id);
          return (
            <button
              key={section.id}
              type="button"
              role="switch"
              aria-checked={isOn}
              onClick={() => toggle(section.id)}
              className={`flex items-start gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                isOn
                  ? 'border-orange-500/70 bg-orange-950/30'
                  : 'border-gray-700 bg-gray-900/40 hover:border-gray-600'
              }`}
            >
              <span
                className={`mt-0.5 w-8 h-[18px] rounded-full flex-shrink-0 transition-colors ${
                  isOn ? 'bg-orange-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`block w-3.5 h-3.5 bg-white rounded-full mt-[2px] transition-transform ${
                    isOn ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </span>
              <span className="min-w-0">
                <span className={`block text-xs font-medium ${isOn ? 'text-orange-200' : 'text-gray-300'}`}>
                  {section.title}
                </span>
                <span className="block text-[11px] text-gray-500 leading-tight">{section.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const TemplateSelectionScreen = ({ onSelectTemplate, onImportTemplate }) => {
  // Every template the screen can offer: the presets plus the comprehensive
  // demo built by slideTemplate.
  const allTemplates = useMemo(() => ([
    ...getPresetTemplates(),
    {
      id: 'comprehensive',
      name: 'Comprehensive Demo',
      description: 'A full-featured template showcasing all content blocks and layouts - great for exploring capabilities',
      thumbnail: 'comprehensive',
      category: 'comprehensive',
      headerColor: '#545f7d',
      bodyBackground: 'white',
      create: createSlideTemplate
    }
  ]), []);

  const defaultTemplate = allTemplates.find((template) => template.id === DEFAULT_TEMPLATE_ID) || null;

  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate);
  const [searchQuery, setSearchQuery] = useState('');
  const [openShelves, setOpenShelves] = useState({});
  const [bopppsSections, setBopppsSections] = useState([]);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [customImage, setCustomImage] = useState(null);
  const [selectedTitleCardBackground, setSelectedTitleCardBackground] = useState(null);
  const [customTitleCardImage, setCustomTitleCardImage] = useState(null);
  const [selectedHeaderColor, setSelectedHeaderColor] = useState(null);
  const [includeReferenceList, setIncludeReferenceList] = useState(true);
  // Open by default: the backgrounds and header colour are part of setting a
  // deck up, and a panel that has to be found first reads as an extra step.
  const [showDesignOptions, setShowDesignOptions] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_START);
  // Set when a chosen file could not be opened; rendered as a full failure
  // page rather than an alert, so the reason is still on screen while the
  // instructor goes looking for the right file.
  const [loadError, setLoadError] = useState(null);
  const fileInputRef = useRef(null);

  const isDefaultTemplateSelected = selectedTemplate?.id === DEFAULT_TEMPLATE_ID;
  // The library tab only offers to start once a template has been picked in it;
  // until then the selection is still the blank deck, which belongs to the
  // other tab and must not be started from this one by accident.
  const hasLibrarySelection = Boolean(selectedTemplate) && !isDefaultTemplateSelected;

  // The library holds everything except the default deck, which has its own
  // place at the top of the screen.
  const libraryShelves = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = (template, group) => query === ''
      || template.name.toLowerCase().includes(query)
      || template.description.toLowerCase().includes(query)
      || group.title.toLowerCase().includes(query)
      || group.description.toLowerCase().includes(query);

    return EXPORT_GROUPS.map((group) => ({
      ...group,
      templates: allTemplates
        .filter((template) => template.id !== DEFAULT_TEMPLATE_ID)
        .filter((template) => getTemplateExportGroup(template).id === group.id)
        .filter((template) => matchesSearch(template, group))
        .sort((a, b) => group.templateIds.indexOf(a.id) - group.templateIds.indexOf(b.id))
    })).filter((group) => group.templates.length > 0);
  }, [allTemplates, searchQuery]);

  const isSearching = searchQuery.trim() !== '';
  // A search opens the shelves that matched, so results are never hidden
  // behind a collapsed header.
  const isShelfOpen = (groupId) => (isSearching ? true : Boolean(openShelves[groupId]));
  const toggleShelf = (groupId) => setOpenShelves((prev) => ({ ...prev, [groupId]: !prev[groupId] }));

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Reset background selection when changing templates
    setSelectedBackground(null);
    setCustomImage(null);
    setSelectedTitleCardBackground(null);
    setCustomTitleCardImage(null);
    setSelectedHeaderColor(null);
  };

  // Each tab starts the deck it is about, so opening the first one puts the
  // blank deck back in hand no matter what was picked in the library.
  const handleTabSelect = (tabId) => {
    setActiveTab(tabId);
    if (tabId === TAB_START && defaultTemplate) {
      handleTemplateSelect(defaultTemplate);
    }
  };

  const handleBackgroundSelect = (background) => {
    if (background && typeof background === 'object') {
      // Custom uploaded image
      setSelectedBackground(background);
      setCustomImage(background);
    } else {
      // Preset background or null (default)
      setSelectedBackground(background);
      if (!background) {
        setCustomImage(null);
      }
    }
  };

  const handleCustomUpload = (imageData) => {
    setSelectedBackground(imageData);
    setCustomImage(imageData);
  };

  const handleTitleCardBackgroundSelect = (background) => {
    if (background && typeof background === 'object') {
      setSelectedTitleCardBackground(background);
      setCustomTitleCardImage(background);
    } else {
      setSelectedTitleCardBackground(background);
      if (!background || background === 'use-slide-background') {
        setCustomTitleCardImage(null);
      }
    }
  };

  const handleTitleCardCustomUpload = (imageData) => {
    setSelectedTitleCardBackground(imageData);
    setCustomTitleCardImage(imageData);
  };

  const handleStartWithTemplate = () => {
    if (selectedTemplate) {
      const templateData = selectedTemplate.create({
        includeReferenceList,
        // BOPPPS scaffolding is a setting of the blank PowerPoint deck only.
        ...(isDefaultTemplateSelected ? { bopppsSections } : {})
      });

      // Determine the body background to use
      let bodyBackgroundToUse = selectedTemplate.bodyBackground || 'white';
      if (selectedBackground) {
        bodyBackgroundToUse = selectedBackground;
      }

      // Determine the title card background
      let titleCardBackgroundToUse = null;
      if (selectedTitleCardBackground === 'use-slide-background') {
        // Use the same as slide background
        titleCardBackgroundToUse = bodyBackgroundToUse;
      } else if (selectedTitleCardBackground) {
        // Use specific title card background
        titleCardBackgroundToUse = selectedTitleCardBackground;
      }
      // If null, title cards will use their default color

      onSelectTemplate({
        ...templateData,
        slideHeaderColor: selectedHeaderColor || selectedTemplate.headerColor || '#545f7d',
        bodyBackground: bodyBackgroundToUse,
        titleCardBackground: titleCardBackgroundToUse
      });
    }
  };

  const handleImportClick = () => {
    setLoadError(null);
    openFileInput(fileInputRef.current);
  };

  /**
   * Read the file the user picked for "Load Presentation" / "Import Template".
   *
   * Both the parse and the shape check happen here, before anything is handed
   * to the editor. They used to be skipped: any JSON at all was passed straight
   * through, so a worksheet, an HTML export's sidecar, or a half-written reply
   * from an assistant would "load" and leave the instructor staring at an empty
   * deck. A file that cannot become a deck now stops here and says why.
   */
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const { data } = parsePresentationText(e.target.result);
          // The editor mounts with the deck object alone, so the file's name —
          // and the version in it — has to be left somewhere it can be picked
          // up. See deckVersioning.rememberOpenedFileName.
          rememberOpenedFileName(file.name, data);
          onImportTemplate(data);
        } catch (error) {
          console.error('[TemplateSelectionScreen] Could not open the selected file:', error);
          setLoadError(describeLoadError(error, { fileName: file.name, source: 'file' }));
        }
      };
      reader.onerror = () => {
        const error = new OpenPresentationError(
          OpenPresentationErrorType.FILE_READ_ERROR,
          'Failed to read the selected file',
          reader.error,
          { fileName: file.name }
        );
        console.error('[TemplateSelectionScreen] Could not read the selected file:', reader.error);
        setLoadError(describeLoadError(error, { fileName: file.name, source: 'file' }));
      };
      reader.readAsText(file);
    }
    // Reset input so the same file can be selected again
    event.target.value = '';
  };

  // Get the effective background for preview
  const getEffectiveBackground = () => {
    if (selectedBackground) {
      return selectedBackground;
    }
    return selectedTemplate?.bodyBackground || 'white';
  };

  // CSS styles for the "Same as slide" tile in the title-card picker, so it
  // mirrors whatever slide background is currently chosen.
  const slideBackgroundForPreview = selectedBackground || selectedTemplate?.bodyBackground;
  const slideBackgroundPreviewStyles = slideBackgroundForPreview
    ? (typeof slideBackgroundForPreview === 'object'
        ? { backgroundImage: `url(${slideBackgroundForPreview.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : getBackgroundStyles(slideBackgroundForPreview))
    : { backgroundColor: '#374151' };

  // Everything that turns the chosen template into a deck: what is selected,
  // the reference-list switch, the design options and Start. Defined once and
  // rendered inside whichever tab is open, so the screen never shows two Start
  // buttons competing with each other.
  const startControls = selectedTemplate && (
    <>
      {/* Four items never fit a phone's width, so they stack: the buttons keep
          their own row and Start stays the largest thing in it. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-center gap-3 min-w-0 sm:flex-1">
          <div
            className="w-16 h-10 rounded-md border border-gray-600 overflow-hidden flex-shrink-0"
            style={getBackgroundStyles(getEffectiveBackground())}
          >
            <div
              className="h-2 w-full"
              style={{ backgroundColor: selectedHeaderColor || selectedTemplate.headerColor || '#545f7d' }}
            />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-100 text-sm">
              {[selectedTemplate.name, selectedTemplate.subtitle].filter(Boolean).join(' · ')}
            </h3>
            <p className="text-xs text-gray-400 truncate max-w-md">{selectedTemplate.description}</p>
          </div>
        </div>

        <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer min-w-0 sm:items-center">
          <input
            type="checkbox"
            checked={includeReferenceList}
            onChange={(e) => setIncludeReferenceList(e.target.checked)}
            className="h-4 w-4 mt-0.5 flex-shrink-0 rounded border-gray-600 text-blue-600 focus:ring-blue-500 sm:mt-0"
          />
          <span className="min-w-0">
            Include Reference List block
            <span className="block text-[11px] text-gray-500">Auto-creates when citations are applied</span>
          </span>
        </label>

        {/* `sm:contents` dissolves this wrapper back into the row on wider
            screens, so the desktop layout stays one line. */}
        <div className="flex items-center gap-2 sm:contents">
          {/* Shown from the start, and foldable away for anyone who has
              already settled on how their decks look. */}
          <button
            type="button"
            onClick={() => setShowDesignOptions((open) => !open)}
            aria-expanded={showDesignOptions}
            className="flex flex-1 items-center justify-center gap-2 px-4 py-3 rounded-lg border border-gray-600 bg-gray-700/60 text-gray-200 hover:bg-gray-700 transition-colors text-sm whitespace-nowrap sm:flex-none sm:py-2.5 sm:flex-shrink-0"
          >
            <Palette className="w-4 h-4" />
            Design options
            <ChevronDown className={`w-4 h-4 transition-transform ${showDesignOptions ? 'rotate-180' : ''}`} />
          </button>

          <button
            onClick={handleStartWithTemplate}
            className="flex flex-1 items-center justify-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold text-base hover:bg-blue-700 transition-colors shadow-md whitespace-nowrap sm:flex-none sm:flex-shrink-0"
          >
            Start
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Backgrounds and header color: title card, slide, then header */}
      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 ${showDesignOptions ? '' : 'hidden'}`}>
        <BackgroundPicker
          label="Title Card Background"
          hint="Applied to the title and section cards"
          icon={Square}
          accent="purple"
          defaultLabel="Default color"
          selectedBackground={selectedTitleCardBackground}
          onSelectBackground={handleTitleCardBackgroundSelect}
          onUploadCustom={handleTitleCardCustomUpload}
          customImage={customTitleCardImage}
          customId="custom-title"
          sameAsSlide={{ styles: slideBackgroundPreviewStyles }}
        />
        <BackgroundPicker
          label="Slide Background"
          hint="Applied to every content slide"
          icon={Image}
          accent="blue"
          defaultLabel="Template default"
          selectedBackground={selectedBackground}
          onSelectBackground={handleBackgroundSelect}
          onUploadCustom={handleCustomUpload}
          customImage={customImage}
          customId="custom"
        />
        <HeaderColorPicker
          label="Slide Header"
          hint="Color of each slide's header bar"
          accent="amber"
          templateDefaultColor={selectedTemplate.headerColor || '#545f7d'}
          selectedColor={selectedHeaderColor}
          onSelectColor={setSelectedHeaderColor}
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950">
      {/* A file that would not open. It sits over the launch screen rather than
          replacing it, so "choose another file" and the template library are
          both one click away. */}
      {loadError && (
        <PresentationLoadErrorScreen
          error={loadError}
          variant="page"
          onRetry={handleImportClick}
          onDismiss={() => setLoadError(null)}
        />
      )}

      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
          {/* Stacks on a phone: side by side, the blurb was squeezed into a
              column two words wide. */}
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              {/* This screen is always dark, so it only needs the dark lockup. */}
              <h1>
                <img
                  src={`${process.env.PUBLIC_URL}/lessonplanner-one-logo-dark.png`}
                  alt="LessonPlanner One"
                  width={1910}
                  height={364}
                  className="h-10 w-auto sm:h-12"
                />
              </h1>
              <p className="text-gray-400 mt-2">Two ways in: start a blank slide deck, or pick a ready-made one from the template library.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept={JSON_ACCEPT}
                className={HIDDEN_FILE_INPUT_CLASS}
              />
              <button
                onClick={handleImportClick}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap"
              >
                <FolderOpen className="w-5 h-5" />
                Load Presentation
              </button>
              <button
                onClick={handleImportClick}
                className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 font-medium hover:bg-gray-600 hover:border-gray-500 transition-colors whitespace-nowrap"
              >
                <Upload className="w-5 h-5" />
                Import Template
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Two ways in, side by side: start from the blank deck, or browse the
            library. Everything a tab needs to reach Start lives inside it. */}
        <div role="tablist" aria-label="How to start" className="flex flex-col gap-3 sm:flex-row mb-6">
          <StartTab
            id={TAB_START}
            icon={Rocket}
            title="Start a slide deck"
            description="Blank 16:9 PowerPoint deck — pick your sections and go"
            isActive={activeTab === TAB_START}
            onSelect={() => handleTabSelect(TAB_START)}
          />
          <StartTab
            id={TAB_LIBRARY}
            icon={LayoutGrid}
            title="Slide Template Library"
            description="Ready-made starting points, grouped by how they export"
            isActive={activeTab === TAB_LIBRARY}
            onSelect={() => handleTabSelect(TAB_LIBRARY)}
          />
        </div>

        {/* Tab 1 — the blank deck, its sections, and Start */}
        {activeTab === TAB_START && defaultTemplate && (
          <section
            role="tabpanel"
            id={`template-tabpanel-${TAB_START}`}
            aria-labelledby={`template-tab-${TAB_START}`}
            className="rounded-2xl border border-orange-800/50 bg-gray-900/50 p-5"
          >
            <div className="flex items-start gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${getGroupAccentClasses('orange')}`}>
                <Square className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-100">
                  Start here · {[defaultTemplate.name, defaultTemplate.subtitle].filter(Boolean).join(' ')}
                </h2>
                <p className="text-sm text-gray-400 mt-1 max-w-3xl">
                  A single empty 16:9 canvas slide, selected by default. Build the deck slide by slide, or
                  switch on the BOPPPS sections you want to start with.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <TemplateThumbnail
                template={defaultTemplate}
                isSelected={isDefaultTemplateSelected}
                onClick={() => handleTemplateSelect(defaultTemplate)}
                customBackground={isDefaultTemplateSelected ? getEffectiveBackground() : null}
              />
              <div className="lg:col-span-2">
                <BopppsSectionPicker selectedIds={bopppsSections} onChange={setBopppsSections} />
              </div>
            </div>

            <ExportSummary />

            {/* Start, in the tab that offers it — no hunting for a floating bar */}
            <div className="mt-6 rounded-xl border border-blue-800/50 bg-blue-950/20 p-4 space-y-4">
              {startControls}
            </div>
          </section>
        )}

        {/* Tab 2 — every template, grouped into shelves */}
        {activeTab === TAB_LIBRARY && (
          <section
            role="tabpanel"
            id={`template-tabpanel-${TAB_LIBRARY}`}
            aria-labelledby={`template-tab-${TAB_LIBRARY}`}
          >
            <div className="rounded-2xl border border-gray-800 bg-gray-900/40">
              <div className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-gray-800">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-100">Slide Template Library</h2>
                  <p className="text-sm text-gray-400">
                    Ready-made starting points, grouped by how they export. Open a shelf, pick a template, then press Start.
                  </p>
                </div>
                <div className="flex-1 min-w-[12rem] max-w-xs ml-auto">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-700 bg-gray-800 text-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {libraryShelves.map((group) => (
                <LibraryShelf
                  key={group.id}
                  group={group}
                  isOpen={isShelfOpen(group.id)}
                  onToggle={() => toggleShelf(group.id)}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {group.templates.map((template) => (
                      <TemplateThumbnail
                        key={template.id}
                        template={template}
                        isSelected={selectedTemplate?.id === template.id}
                        onClick={() => handleTemplateSelect(template)}
                        customBackground={selectedTemplate?.id === template.id ? getEffectiveBackground() : null}
                      />
                    ))}
                  </div>
                  <div className={`mt-5 rounded-xl border p-4 text-sm leading-6 ${getGroupAccentClasses(group.accent)}`}>
                    <span className="font-semibold text-gray-100">How this shelf works: </span>
                    <span className="text-gray-300">{group.detail}</span>
                  </div>
                </LibraryShelf>
              ))}

              {libraryShelves.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-gray-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-200 mb-2">No templates found</h3>
                  <p className="text-gray-400">Try a different search term</p>
                </div>
              )}
            </div>

            {/* The library's own Start: a bar that appears once a template has
                been picked, and a prompt saying what to do until then. */}
            {hasLibrarySelection ? (
              <div className="mt-6 rounded-xl border border-blue-800/50 bg-blue-950/20 p-4 space-y-4">
                {startControls}
              </div>
            ) : (
              <p className="mt-6 rounded-xl border border-dashed border-gray-700 bg-gray-900/40 p-4 text-sm text-gray-400">
                Pick a template above to set it up and start, or switch back to
                <button
                  type="button"
                  onClick={() => handleTabSelect(TAB_START)}
                  className="mx-1 text-blue-400 hover:text-blue-300 underline underline-offset-2"
                >
                  Start a slide deck
                </button>
                for a blank 16:9 deck.
              </p>
            )}
          </section>
        )}
      </div>

      {/* Quick Start Tips */}
      <div className="bg-gray-800 border-t border-gray-700 mt-8">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <h2 className="text-lg font-semibold text-gray-100 mb-4">Quick Start Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-4 p-4 bg-blue-900/30 rounded-lg border border-blue-800/40">
              <div className="w-10 h-10 bg-blue-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-100">Start Fresh</h3>
                <p className="text-sm text-gray-400 mt-1">The first tab is ready to go — switch on the BOPPPS sections you want and press Start</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-purple-900/30 rounded-lg border border-purple-800/40">
              <div className="w-10 h-10 bg-purple-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Image className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-100">Custom Backgrounds</h3>
                <p className="text-sm text-gray-400 mt-1">Design options sits beside Start — set the slide, title card and header colours before you begin</p>
              </div>
            </div>
            <button
              onClick={handleImportClick}
              className="flex items-start gap-4 p-4 bg-green-900/30 rounded-lg border border-green-800/40 hover:bg-green-900/40 transition-colors cursor-pointer text-left w-full"
            >
              <div className="w-10 h-10 bg-green-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-100">Continue Working</h3>
                <p className="text-sm text-gray-400 mt-1">Load a previously saved presentation to continue where you left off</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionScreen;
